import React from "react";
import { Box, Typography, Button as MuiButton, Checkbox } from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

// Shared visual tokens for the Work Transfer module — lifted directly from
// CP Payout (Components/CPPayout/CpPayout.jsx) so the two modules read as one
// design system: same palette, same font-size scale, same labeled-filter-box
// treatment, instead of Work Transfer's original bare MUI defaults.
export const COLORS = {
  border: "#e5e7eb",
  textSecondary: "#6b7280",
  blue: "#2563eb",
  blueLight: "#dbeafe",
  orange: "#f59e0b",
  green: "#059669",
  red: "#dc2626",
  sunken: "#f9fafb",
};

// Round checkbox, not MUI's default rounded-square (2026-08-24 request) —
// swaps in circular icons rather than CSS-clipping the square glyph, which
// would just cut the corners off the checkmark. RemoveCircleIcon covers the
// indeterminate (3-state) case bucket/approval headers use — MUI's Checkbox
// has no built-in indeterminate icon slot, so it's picked manually below.
export function CircleCheckbox({ indeterminate, checked, sx, ...props }) {
  return (
    <Checkbox
      {...props}
      checked={indeterminate ? true : checked}
      icon={<RadioButtonUncheckedIcon />}
      checkedIcon={indeterminate ? <RemoveCircleIcon /> : <CheckCircleIcon />}
      sx={{ color: COLORS.textSecondary, "&.Mui-checked": { color: COLORS.blue }, ...sx }}
    />
  );
}

export function PageHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography fontSize={20} fontWeight={700} lineHeight={1.2}>
        {title}
      </Typography>
      {subtitle && (
        <Typography fontSize={12} color={COLORS.textSecondary} mt={0.3}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export function FieldLabel({ children }) {
  return (
    <Typography fontSize={12} color={COLORS.textSecondary} mb={0.5}>
      {children}
    </Typography>
  );
}

export function Field({ label, sx, children }) {
  return (
    <Box sx={sx}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </Box>
  );
}

export function PrimaryButton({ sx, ...props }) {
  return (
    <MuiButton
      {...props}
      sx={{
        bgcolor: COLORS.blue,
        color: "#fff",
        fontSize: 14,
        fontWeight: 500,
        textTransform: "none",
        borderRadius: 2,
        px: 2.5,
        py: 1,
        boxShadow: "none",
        "&:hover": { bgcolor: "#1d4ed8", boxShadow: "none" },
        "&.Mui-disabled": { bgcolor: "#93c5fd", color: "#fff" },
        ...sx,
      }}
    />
  );
}

export function SecondaryButton({ sx, ...props }) {
  return (
    <MuiButton
      {...props}
      sx={{
        bgcolor: "#fff",
        color: "#111827",
        fontSize: 14,
        fontWeight: 500,
        textTransform: "none",
        borderRadius: 2,
        px: 2.5,
        py: 1,
        border: `1px solid ${COLORS.border}`,
        boxShadow: "none",
        "&:hover": { bgcolor: COLORS.sunken, boxShadow: "none" },
        ...sx,
      }}
    />
  );
}

// Applied to Autocomplete/TextField `sx` — renders the CP-Payout-style boxed
// field (border e5e7eb, borderRadius 2, fontSize 14). Pair with <Field label=…>
// instead of MUI's built-in floating label (drop the `label` prop on TextField).
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    fontSize: 14,
    fontWeight: 500,
    bgcolor: "#fff",
    "& fieldset": { borderColor: COLORS.border },
    "&:hover fieldset": { borderColor: COLORS.border },
    "&.Mui-focused fieldset": { borderColor: COLORS.blue, borderWidth: 1 },
  },
  "& .MuiInputBase-input": { fontSize: 14, padding: "9.5px 8px !important" },
};

export const cardSx = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 3,
  boxShadow: "none",
};
